// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    CONFIRM_CODE: '/auth/confirm-code',
    SET_NEW_PASSWORD: '/auth/set-new-password',
    LINKEDIN_AUTH_URL: '/auth/linkedin/auth-url',
    LINKEDIN_SIGNIN: '/auth/linkedin/signin',
  },

  // Onboarding
  ONBOARDING: {
    BASIC_DETAILS: '/onboarding/basic-details',
    COMPANY_DETAILS: '/onboarding/company-details',
    COMPANY_INTRO: '/onboarding/company-intro-vision',
    VERIFICATION: '/onboarding/verification',
    ACTIVATE:'/onboarding/activate',
    COMPANY_PITCH_VIDEO: '/onboarding/add-company-pitch-video',
    CREDITS: '/onboarding/credits',
    PROGRESS: '/onboarding/progress',
    COMPLETE: '/onboarding/complete',
  },

  // Common Data
  COMMON: {
    DESIGNATIONS: '/common/get-designation',
    EXPERIENCE: '/common/get-experience',
    SKILLS: '/common/get-skills',
  },

  // Profile
  PROFILE: {
    GET_PROFILE: '/profile/get-profile',
    GET_COMPANIES: '/profile/get-companies',
  },

  // Users
  USERS: {
    GET_CANDIDATE_LIST: '/users/get-candidatelist',
    GET_CANDIDATE_DETAIL: '/users/get-candidatedetail',
  },

  // Credits
  CREDITS: {
    BALANCE: '/credits/balance',
    FEATURES: '/credits/features',
  },

  // Chat
  CHAT: {
    GET_MATCH_LIST: '/chat/get-match-list',
  },
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  REMEMBERED_EMAIL: 'rememberedEmail',
  SOCIAL_TYPE: 'social_type',
  LOGIN_WITH_GOOGLE: 'loginWithGoogle',

  // Onboarding
  ONBOARDING_BASIC_DETAIL: 'onboarding_BasicDetail',
  ONBOARDING_COMPANY_DETAILS: 'onboarding_CompanyDetails',
  ONBOARDING_COMPANY_INTRO: 'onboarding_CompanyIntro',
  ONBOARDING_VERIFICATION: 'onboarding_verification',
  ONBOARDING_ACTIVATE: 'onboarding_activate',
} as const;

// Cookie Configuration
export const COOKIE_CONFIG = {
  AUTH_TOKEN: {
    expires: 1, // 1 day
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict' as const,
    httpOnly: false, // Set to true in production with proper backend setup
  },
  REFRESH_TOKEN: {
    expires: 7, // 7 days
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict' as const,
    httpOnly: false,
  },
} as const;

// Cache TTL (Time To Live) in milliseconds
export const CACHE_TTL = {
  SHORT: 2 * 60 * 1000, // 2 minutes
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 15 * 60 * 1000, // 15 minutes
  VERY_LONG: 30 * 60 * 1000, // 30 minutes
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNAUTHORIZED: 'Session expired. Please login again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  FILE_TOO_LARGE: 'File size is too large.',
  INVALID_FILE_TYPE: 'Invalid file type.',
  GENERIC_ERROR: 'Something went wrong. Please try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login successful',
  LOGOUT_SUCCESS: 'Logged out successfully',
  PROFILE_UPDATED: 'Profile updated successfully',
  PASSWORD_RESET_SENT: 'Password reset code sent to your email',
  PASSWORD_UPDATED: 'Password updated successfully',
  ONBOARDING_COMPLETE: 'Welcome to techNbuddy! Your onboarding is complete.',
  DATA_SAVED: 'Data saved successfully',
} as const;

// Validation Rules
export const VALIDATION_RULES = {
  EMAIL: {
    PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MAX_LENGTH: 254,
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    PATTERN: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
  },
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 50,
    PATTERN: /^[a-zA-Z\s\-'\.]+$/,
  },
  PHONE: {
    PATTERN: /^\+?[\d\s\-\(\)]{10,}$/,
  },
  SEARCH_TERM: {
    MAX_LENGTH: 100,
  },
} as const;

// File Upload Limits
export const FILE_UPLOAD = {
  MAX_SIZE: {
    IMAGE: 5 * 1024 * 1024, // 5MB
    VIDEO: 50 * 1024 * 1024, // 50MB
    DOCUMENT: 10 * 1024 * 1024, // 10MB
  },
  ALLOWED_TYPES: {
    IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    VIDEO: ['video/mp4', 'video/webm', 'video/ogg'],
    DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  },
  ALLOWED_EXTENSIONS: {
    IMAGE: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    VIDEO: ['mp4', 'webm', 'ogg'],
    DOCUMENT: ['pdf', 'doc', 'docx'],
  },
} as const;

// Onboarding Steps
export const ONBOARDING_STEPS = {
  BASIC_DETAILS: 'basic_details',
  COMPANY_DETAILS: 'company_details',
  COMPANY_INTRO: 'company_intro',
  VERIFICATION:'verification',
  ACTIVATE:'activate',
} as const;

// Onboarding Step Order
export const ONBOARDING_STEP_ORDER = [
  ONBOARDING_STEPS.BASIC_DETAILS,
  ONBOARDING_STEPS.COMPANY_DETAILS,
  ONBOARDING_STEPS.COMPANY_INTRO,
  ONBOARDING_STEPS.VERIFICATION,
  ONBOARDING_STEPS.ACTIVATE,
] as const;

// Onboarding URLs
export const ONBOARDING_URLS = {
  [ONBOARDING_STEPS.BASIC_DETAILS]: '/onboarding/basic-details',
  [ONBOARDING_STEPS.COMPANY_DETAILS]: '/onboarding/company-details',
  [ONBOARDING_STEPS.COMPANY_INTRO]: '/onboarding/company-intro',
  [ONBOARDING_STEPS.VERIFICATION]: '/onboarding/verification',
  [ONBOARDING_STEPS.ACTIVATE]: '/onboarding/activate',
} as const;

// Social Login Types
export const SOCIAL_TYPES = {
  EMAIL: 'email',
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
} as const;

// Rate Limiting
export const RATE_LIMITS = {
  LOGIN_ATTEMPTS: {
    MAX_ATTEMPTS: 5,
    WINDOW_MS: 15 * 60 * 1000, // 15 minutes
  },
  PASSWORD_RESET: {
    MAX_ATTEMPTS: 3,
    WINDOW_MS: 60 * 60 * 1000, // 1 hour
  },
  SEARCH_REQUESTS: {
    MAX_ATTEMPTS: 10,
    WINDOW_MS: 60 * 1000, // 1 minute
  },
} as const;

// Retry Configuration
export const RETRY_CONFIG = {
  DEFAULT: {
    RETRIES: 3,
    RETRY_DELAY: 1000,
  },
  UPLOAD: {
    RETRIES: 2,
    RETRY_DELAY: 2000,
  },
} as const;

// Debounce Delays
export const DEBOUNCE_DELAYS = {
  SEARCH: 300,
  AUTO_SAVE: 1000,
  RESIZE: 250,
  SCROLL: 100,
} as const;

// Theme Colors (for loading spinners, buttons, etc.)
export const THEME_COLORS = {
  PRIMARY: '#007bff',
  SECONDARY: '#6c757d',
  SUCCESS: '#28a745',
  DANGER: '#dc3545',
  WARNING: '#ffc107',
  INFO: '#17a2b8',
  WHITE: '#ffffff',
} as const;

// Breakpoints for responsive design
export const BREAKPOINTS = {
  XS: 0,
  SM: 576,
  MD: 768,
  LG: 992,
  XL: 1200,
  XXL: 1400,
} as const;

// Animation Durations
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
} as const;

// Z-Index Layers
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070,
  TOAST: 1080,
} as const;

// Environment Variables (with defaults)
export const ENV = {
  NODE_ENV: process.env.NODE_ENV || 'development',
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://api.technbuddy.com/techbuddy/v2/enterprise',
  GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  LINKEDIN_CLIENT_ID: process.env.REACT_APP_LINKEDIN_CLIENT_ID || '',
  AWS_REGION: process.env.REACT_APP_AWS_REGION || '',
  AWS_BUCKET_NAME: process.env.REACT_APP_AWS_BUCKET_NAME || '',
} as const;

// LinkedIn Configuration
export const LINKEDIN_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_LINKEDIN_CLIENT_ID ,
  CLIENT_SECRET: process.env.REACT_APP_LINKEDIN_CLIENT_SECRET ,
  REDIRECT_URL : process.env.REACT_APP_LINKEDIN_REDIRECT_URI,
  SCOPE: 'openid profile email',
  AUTH_URL: 'https://www.linkedin.com/oauth/v2/authorization',
  TOKEN_URL: 'https://www.linkedin.com/oauth/v2/accessToken',
  API_BASE_URL: 'https://api.linkedin.com/v2',
} as const;

// Feature Flags
export const FEATURE_FLAGS = {
  ENABLE_LINKEDIN_LOGIN: true, // Disabled due to app configuration issues
  ENABLE_AUTO_SAVE: true,
  ENABLE_OFFLINE_MODE: false,
  ENABLE_ANALYTICS: process.env.NODE_ENV === 'production',
  ENABLE_ERROR_REPORTING: process.env.NODE_ENV === 'production',
} as const;